/**
 * The peek stack in the URL: `peek=rec:<cls>:<key>` · `doc:<vaultId>:<uuid>[:<day>]` · `run:<executionId>` · `sys:<code>` ·
 * `int:<process>` (a system and an integration, from Now's map and feed), two deep
 * as `a|b` (a run opened from a record keeps the record behind it). Every other search param stays as it is, so the
 * stage's query state (q, step, range, from, to, asOf, lens) is untouched by opening and closing.
 */
import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useSearchParams } from '../lib/liveparams.ts';
import { canStepBack, clearPeekView, closeSteps, pushedCount, readPeekCount } from '../lib/viewstate.ts';

/** A stored message by where it is: the same shape as components/MessageWindow.tsx's, kept here so the peeks outlive it. */
export interface MessageRef { vaultId: string; uuid: string; day?: string }

export type PeekRef =
  | { kind: 'record'; cls: string; key: string }
  | { kind: 'document'; ref: MessageRef }
  | { kind: 'run'; executionId: string }
  | { kind: 'system'; code: string }
  | { kind: 'integration'; process: string };

export const PEEK_PARAM = 'peek';
/** How deep peeks stack: the one open and the one it was opened from. */
export const PEEK_DEPTH = 2;

// '|' separates the stack and ':' the parts, so a record key carrying either is escaped by hand (the URL's own
// percent-encoding is URLSearchParams' business and would be undone before this runs)
const esc = (s: string) => s.replace(/%/g, '%25').replace(/\|/g, '%7C').replace(/:/g, '%3A');
const unesc = (s: string) => s.replace(/%3A/g, ':').replace(/%7C/g, '|').replace(/%25/g, '%');

export function encodePeek(r: PeekRef): string {
  switch (r.kind) {
    case 'record': return `rec:${r.cls}:${esc(r.key)}`;
    case 'document': return `doc:${r.ref.vaultId}:${r.ref.uuid}${r.ref.day ? `:${r.ref.day}` : ''}`;
    case 'run': return `run:${r.executionId}`;
    case 'system': return `sys:${esc(r.code)}`;
    case 'integration': return `int:${esc(r.process)}`;
  }
}

export function parsePeek(s: string): PeekRef | null {
  const i = s.indexOf(':');
  if (i < 0) return null;
  const kind = s.slice(0, i);
  const rest = s.slice(i + 1);
  if (kind === 'rec') {
    const j = rest.indexOf(':');
    if (j <= 0 || j === rest.length - 1) return null;
    return { kind: 'record', cls: rest.slice(0, j), key: unesc(rest.slice(j + 1)) };
  }
  if (kind === 'doc') {
    const [vaultId, uuid, day] = rest.split(':');
    if (!vaultId || !uuid) return null;
    return { kind: 'document', ref: day ? { vaultId, uuid, day } : { vaultId, uuid } };
  }
  if (kind === 'run') return rest ? { kind: 'run', executionId: rest } : null;
  if (kind === 'sys') return rest ? { kind: 'system', code: unesc(rest) } : null;
  if (kind === 'int') return rest ? { kind: 'integration', process: unesc(rest) } : null;
  return null;
}

/** The stack from the param's value, newest last; anything unreadable is dropped. */
export function parseStack(raw: string | null | undefined): PeekRef[] {
  if (!raw) return [];
  return raw.split('|').map(parsePeek).filter((r): r is PeekRef => r !== null).slice(-PEEK_DEPTH);
}

export const samePeek = (a: PeekRef, b: PeekRef) => encodePeek(a) === encodePeek(b);

/**
 * The open peeks and the ways to change them. `open` stacks a peek on the one open (the oldest falls off beyond two);
 * with `replace` it takes the top one's place instead. Opening pushes a history entry and counts the pushes in its
 * state, so closing walks back exactly that far (Back never reopens a peek that was closed) and a peek that came with
 * the link (no count) closes by replacing. The peek's own view state (`praw`, `pf`, `psel`, `pfresh`) goes with the
 * peek on top: a different one starts clean. Each push has a view transition where the browser has them.
 */
export function usePeek() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { state } = useLocation();
  const raw = params.get(PEEK_PARAM);
  const stack = useMemo(() => parseStack(raw), [raw]);
  const count = readPeekCount(state);

  const write = useCallback((next: PeekRef[], how: { replace: boolean; state: unknown }) => {
    setParams((prev) => {
      const p = new URLSearchParams(prev);
      const before = parseStack(p.get(PEEK_PARAM));
      if (next.length) p.set(PEEK_PARAM, next.map(encodePeek).join('|'));
      else p.delete(PEEK_PARAM);
      const top = (s: PeekRef[]) => (s.length ? encodePeek(s[s.length - 1]) : '');
      return top(before) === top(next) ? p : clearPeekView(p);
    }, { viewTransition: true, replace: how.replace, state: how.state });
  }, [setParams]);

  const open = useCallback((ref: PeekRef, replace = false) => {
    const top = stack[stack.length - 1];
    if (top && samePeek(top, ref)) return;
    const next = (replace ? [...stack.slice(0, -1), ref] : [...stack, ref]).slice(-PEEK_DEPTH);
    write(next, replace ? { replace: true, state } : { replace: false, state: { mvPeek: pushedCount(stack.length, count) } });
  }, [stack, state, count, write]);
  const back = useCallback(() => {
    if (canStepBack(count, stack.length)) void navigate(-1);
    else write(stack.slice(0, -1), { replace: true, state });
  }, [stack, state, count, navigate, write]);
  const close = useCallback(() => {
    const n = closeSteps(count);
    if (n > 0) void navigate(-n);
    else write([], { replace: true, state });
  }, [count, state, navigate, write]);

  return { stack, top: stack[stack.length - 1] ?? null, open, back, close };
}
